import {Injectable, Optional} from '@angular/core';
import {TranslationService} from '@ngmedax/translation';
import {TRANSLATION_EDITOR_SCOPE} from '../../../constants';
import {QuestionnaireStateService} from '../../services/questionnaire-state.service';
import {QuestionnaireVariablesService} from '../../services/questionnaire-variables.service';
import {QuestionnaireConditionService} from '../../services/questionnaire-condition.service';
import {buildTemplateVariables} from '../../froala/template-variables';
import {DependencySource} from './dependency-conditions';

/** Shares friendly template labels while limiting dependencies to their supported runtime sources. */
@Injectable()
export class DependencyCatalogService {
  /** Injects current questionnaire mappings and configured patient field definitions. */
  public constructor(private state: QuestionnaireStateService, private variables: QuestionnaireVariablesService,
    private conditions: QuestionnaireConditionService, @Optional() private translation: TranslationService) {}

  /** Converts rich questionnaire titles to inert labels in the active questionnaire language. */
  private title(value: {[locale: string]: string}, locale: string): string {
    const document = new DOMParser().parseFromString(value?.[locale] || value?.de_DE || '', 'text/html');
    return (document.body.textContent || '').replace(/\s+/g, ' ').trim();
  }

  /** Translates administration labels independently of the questionnaire's content language. */
  private translate(text: string): string {
    return this.translation ? this.translation.translate({text, scope: TRANSLATION_EDITOR_SCOPE}) : text;
  }

  /** Builds fresh labels, hierarchy and input hints without modifying any stored dependency. */
  public build(locale: string): DependencySource[] {
    const selection = this.conditions.getSelectData();
    const labels = buildTemplateVariables(this.state.getQuestionnaire(), this.variables.getVariableScopes(), locale,
      text => this.translate(text));
    const additionalLabels = {
      'client.isMale': 'Männlich', 'client.isFemale': 'Weiblich', 'client.isDiverse': 'Divers',
      'client.caseNr': 'Fallnummer', 'client.customerNr': 'Patientennummer', 'client.zipCode': 'Postleitzahl',
      'client.city': 'Ort', 'client.street': 'Straße', 'client.streetNr': 'Hausnummer',
      'client.location': 'Standort', 'client.status': 'Status'
    };
    const sources: DependencySource[] = selection.variables.map(path => {
      const label = labels.find(variable => variable.path === path);
      const container = this.conditions.getContainerForVariable(path);
      const answerHash = path.startsWith('inline.') && this.variables.getMappedPathHash('inline', path.slice(7));
      const format = path.startsWith('scoring.') ? 'numeric' :
        answerHash && selection.answerToQuestionMap[answerHash] ? 'boolean' : container?.format;
      const options = container?.options || {};
      return {
        path, template: path, label: label?.label || this.translate(additionalLabels[path] || path),
        group: label?.group || (path.startsWith('client.custom.') ? 'custom' : path.split('.')[0]),
        questionContext: label?.questionContext,
        valueType: format === 'numeric' ? 'number' : format === 'boolean' ? 'boolean' : format === 'date' ? 'date' : 'text',
        dateInput: options.fullDateTime ? 'datetime-local' : options.yearDate ? 'number' : options.monthDate ? 'month' : 'date',
        choices: this.conditions.getVariableChoices(path)
      };
    });
    selection.questions.forEach(question => {
      const index = this.state.getQuestionnaire().questions.findIndex(item => item.pathHash === question.pathHash);
      const title = this.title(question.title, locale) || `${this.translate('Frage')} ${index + 1}`;
      const answers = selection.questionToAnswerMap[question.pathHash] as any;
      (answers || []).forEach(answer => {
        const path = `container.x${answer.pathHash}`;
        const titlePath = (answer.titlePath || []).map(part => this.title(part, locale)).filter(Boolean);
        const answerTitle = titlePath.length ? titlePath.join(' › ') :
          this.title(answer.title, locale).replace(/ > /g, ' › ') || this.translate('Antwort');
        const answerLabel = titlePath[titlePath.length - 1] || answerTitle;
        sources.push({path, template: path, label: `${title} › ${answerTitle}`,
          group: 'answers', valueType: 'boolean',
          questionContext: {questionId: question.pathHash, questionTitle: title,
            label: answerLabel, ancestors: titlePath.slice(0, -1)}});
      });
    });
    return sources;
  }
}
